import React, { useCallback, useState } from 'react';
import { GenericStep } from '@/components/FullWidthTemplate/GenericStep';
import AlterDomainBR from '@/media/onboarding/temporary-domain-br.png';
import AlterDomainES from '@/media/onboarding/temporary-domain-es.webp';
import { isBRServer } from '@/utils/Validators/validation';
import * as Styles from './SelectPlanListStep.styles';
import { SelectPlanListStepProps } from './SelectPlanListStep.types';
import { Radio, IconGlobeCursor } from 'gatorcomponents';

const SelectPlanListStep = ({
  locales,
  buttonsProps,
  userAvailablePlans,
  selectedObject,
  handleSameDomain,
  configureDns,
  setCurrentHosting,
  loadingSites,
  isButtonLoading,
  userDomainsList,
  addDomainToPlan,
  usingTempDomain,
  handleCreateTempDomain,
  setExecuteAfterTempDomainCreation,
  loadingTempDomainCreation,
}) => {
  const [checkedOption, setCheckedOption] = useState(null);

  const continueRedirecting = useCallback((tempdomain) => {
    if (checkedOption && checkedOption.hosting_id && (tempdomain || (selectedObject && (selectedObject.domain || selectedObject.selectedDomain)))) {
      const hostingId = checkedOption.hosting_id;
      const { domain, selectedDomain } = selectedObject;
      const domainInUse = domain || selectedDomain || tempdomain;

      const itemByHostingIdState = userDomainsList.find(item => item.hosting_id === hostingId);
      const cpanelDomains = (itemByHostingIdState && itemByHostingIdState.cpanel_domains) || null;
      const alreadyExistsCpanel = cpanelDomains && cpanelDomains.some(item => item.domain === domainInUse);

      const isSameDomain = checkedOption.hosting_id === selectedObject.hosting_id;

      setCurrentHosting({
        ...selectedObject,
        domain: domainInUse,
        hosting_id: hostingId,
        domainId: selectedObject.domain_id,
      });

      configureDns({
        domain: domainInUse,
        hostingId,
        domainId: selectedObject.domain_id,
      });

      if (alreadyExistsCpanel || isSameDomain) {
        return handleSameDomain && handleSameDomain({ domain: domainInUse, hostingId });
      }

      if (!alreadyExistsCpanel) {
        addDomainToPlan && addDomainToPlan({ domain: domainInUse, hostingId, selectedObject });
      }
    }
  }, [
    checkedOption,
    selectedObject,
    userDomainsList,
    configureDns,
    setCurrentHosting,
    handleSameDomain,
    addDomainToPlan,
  ]);

  const handleRedirect = useCallback(async () => {
    if (checkedOption && checkedOption.hosting_id && (usingTempDomain || (selectedObject && (selectedObject.domain || selectedObject.selectedDomain)))) {
      const hostingId = checkedOption.hosting_id;

      if (usingTempDomain) {
        handleCreateTempDomain && handleCreateTempDomain({ hostingId });
        setExecuteAfterTempDomainCreation(continueRedirecting);
      }

      if (!usingTempDomain) {
        continueRedirecting();
      }
    }
  }, [
    checkedOption,
    selectedObject,
    usingTempDomain,
    handleCreateTempDomain,
    continueRedirecting,
    setExecuteAfterTempDomainCreation,
  ]);

  const stepProps = useCallback(() => {
    const customButtonsProps = {
      ...buttonsProps,
      rightButton: {
        ...buttonsProps.rightButton,
        testId: 'select-plan-list-step-button',
        disabled: !(checkedOption && checkedOption.hosting_id) || isButtonLoading || loadingTempDomainCreation,
        loading: loadingSites || isButtonLoading || loadingTempDomainCreation,
        onClick: handleRedirect,
      },
    };

    return {
      icon: <IconGlobeCursor color="primaryMedium" />,
      title: locales.title,
      testId: 'select-plan-list-step',
      imageSrc: isBRServer ? AlterDomainBR : AlterDomainES,
      buttonsProps: customButtonsProps,
    };
  }, [
    buttonsProps,
    handleRedirect,
    locales.title,
    loadingSites,
    checkedOption,
    isButtonLoading,
    loadingTempDomainCreation,
  ]);

  return (
    <>
      <GenericStep {...stepProps()}>
        <Styles.OptionsWrapper>
          {userAvailablePlans.map((option) => {
            const id = option.hosting_id;
            return (
              <Styles.ListItem key={id}>
                <Styles.RadioLabel
                  data-testid={`option-${id}`}
                  htmlFor={id}
                  className={checkedOption && checkedOption.hosting_id === id ? 'checked' : ''}
                  onClick={() => setCheckedOption(option)}
                  role="button"
                >
                  <Styles.OptionRow>
                    <Radio
                      id={id}
                      variant="blue"
                      checked={checkedOption && checkedOption.hosting_id === id}
                    />
                    <Styles.OptionTitle>
                      {option.product_name}
                    </Styles.OptionTitle>
                  </Styles.OptionRow>

                  <Styles.OptionDomainContainer>
                    <Styles.OptionDomainText>
                      {locales.domainText}
                    </Styles.OptionDomainText>
                    <Styles.OptionDomain>
                      {option.domain}
                    </Styles.OptionDomain>
                  </Styles.OptionDomainContainer>
                </Styles.RadioLabel>
              </Styles.ListItem>
            );
          })}
        </Styles.OptionsWrapper>
      </GenericStep>
    </>
  );
};

SelectPlanListStep.propTypes = SelectPlanListStepProps;

export default SelectPlanListStep;
